/**
 * EcoF Image Slider
 * 
 * Features:
 * - Manual navigation (arrows)
 * - Auto-scroll with configurable speed
 * - Touch swipe support
 * - Pagination dots
 * - Keyboard navigation
 * - Theme editor support
 */

class EcoFImageSlider {
  constructor(gallery) {
    this.gallery = gallery;
    this.track = gallery.querySelector('.ecof-slider-track');
    this.slides = gallery.querySelectorAll('.ecof-slider-slide');
    this.prevBtn = gallery.querySelector('.ecof-slider-nav-prev');
    this.nextBtn = gallery.querySelector('.ecof-slider-nav-next');
    this.dots = gallery.querySelectorAll('.ecof-slider-dot');
    this.swipeHint = gallery.querySelector('.ecof-slider-swipe-hint');
    
    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    this.autoplayTimer = null;
    this.isAutoplayEnabled = gallery.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(gallery.dataset.autoplaySpeed) * 1000 || 5000;
    
    // Touch/swipe properties
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.isDragging = false;
    this.startPos = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    
    if (this.totalSlides > 1) {
      this.init();
    }
  }

  init() {
    // Event listeners
    this.addEventListeners();
    
    // Start autoplay if enabled
    if (this.isAutoplayEnabled) {
      this.startAutoplay();
    }
    
    // Show first slide
    this.goToSlide(0);
  }

  addEventListeners() {
    // Arrow navigation
    if (this.prevBtn && this.nextBtn) {
      this.prevBtn.addEventListener('click', () => this.prevSlide());
      this.nextBtn.addEventListener('click', () => this.nextSlide());
    }

    // Dot navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });

    // Keyboard navigation
    this.gallery.addEventListener('keydown', (e) => this.handleKeyboard(e));

    // Touch/swipe events
    this.track.addEventListener('touchstart', (e) => this.touchStart(e), { passive: true });
    this.track.addEventListener('touchmove', (e) => this.touchMove(e), { passive: false });
    this.track.addEventListener('touchend', () => this.touchEnd());

    // Mouse drag events (for desktop)
    this.track.addEventListener('mousedown', (e) => this.dragStart(e));
    this.track.addEventListener('mousemove', (e) => this.dragMove(e));
    this.track.addEventListener('mouseup', () => this.dragEnd());
    this.track.addEventListener('mouseleave', () => this.dragEnd());

    // Pause autoplay on hover (desktop)
    this.gallery.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.gallery.addEventListener('mouseleave', () => this.resumeAutoplay());

    // Pause autoplay on focus (accessibility)
    this.gallery.addEventListener('focusin', () => this.pauseAutoplay());
    this.gallery.addEventListener('focusout', () => this.resumeAutoplay());
  }

  goToSlide(index) {
    // Boundary check
    if (index < 0) {
      this.currentIndex = this.totalSlides - 1;
    } else if (index >= this.totalSlides) {
      this.currentIndex = 0;
    } else {
      this.currentIndex = index;
    }

    // Update transform
    const translateX = -this.currentIndex * 100;
    this.track.style.transform = `translateX(${translateX}%)`;
    this.currentTranslate = translateX;
    this.prevTranslate = translateX;

    // Update dots
    this.updateDots();

    // Hide swipe hint after first interaction
    if (this.swipeHint) {
      this.gallery.classList.add('has-interacted');
    }

    // Reset autoplay timer
    if (this.isAutoplayEnabled) {
      this.resetAutoplay();
    }
  }

  nextSlide() {
    this.goToSlide(this.currentIndex + 1);
  }

  prevSlide() {
    this.goToSlide(this.currentIndex - 1);
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      if (index === this.currentIndex) {
        dot.classList.add('active');
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.classList.remove('active');
        dot.removeAttribute('aria-current');
      }
    });
  }

  handleKeyboard(e) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.prevSlide();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.nextSlide();
    }
  }

  // Touch events
  touchStart(e) {
    this.touchStartX = e.touches[0].clientX;
    this.isDragging = true;
    this.startPos = e.touches[0].clientX;
    
    // Hide swipe hint
    if (this.swipeHint) {
      this.gallery.classList.add('has-interacted');
    }
  }

  touchMove(e) {
    if (!this.isDragging) return;

    const currentPosition = e.touches[0].clientX;
    const diff = currentPosition - this.startPos;
    this.currentTranslate = this.prevTranslate + (diff / this.gallery.offsetWidth) * 100;

    // Prevent default only if moving horizontally
    if (Math.abs(diff) > 10) {
      e.preventDefault();
    }

    // Apply transform with boundary constraints
    const maxTranslate = 0;
    const minTranslate = -(this.totalSlides - 1) * 100;
    const constrainedTranslate = Math.max(minTranslate, Math.min(maxTranslate, this.currentTranslate));
    
    this.track.style.transform = `translateX(${constrainedTranslate}%)`;
  }

  touchEnd() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    const movedBy = this.currentTranslate - this.prevTranslate;

    // Threshold for slide change (20% of width)
    if (movedBy < -20) {
      this.nextSlide();
    } else if (movedBy > 20) {
      this.prevSlide();
    } else {
      // Snap back to current slide
      this.goToSlide(this.currentIndex);
    }
  }

  // Mouse drag events (similar to touch)
  dragStart(e) {
    this.isDragging = true;
    this.startPos = e.clientX;
    this.track.style.cursor = 'grabbing';
    e.preventDefault(); // Prevent text selection
  }

  dragMove(e) {
    if (!this.isDragging) return;

    const currentPosition = e.clientX;
    const diff = currentPosition - this.startPos;
    this.currentTranslate = this.prevTranslate + (diff / this.gallery.offsetWidth) * 100;

    const maxTranslate = 0;
    const minTranslate = -(this.totalSlides - 1) * 100;
    const constrainedTranslate = Math.max(minTranslate, Math.min(maxTranslate, this.currentTranslate));
    
    this.track.style.transform = `translateX(${constrainedTranslate}%)`;
  }

  dragEnd() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.track.style.cursor = 'grab';
    
    const movedBy = this.currentTranslate - this.prevTranslate;

    if (movedBy < -20) {
      this.nextSlide();
    } else if (movedBy > 20) {
      this.prevSlide();
    } else {
      this.goToSlide(this.currentIndex);
    }
  }

  // Autoplay methods
  startAutoplay() {
    if (!this.isAutoplayEnabled) return;
    
    this.autoplayTimer = setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
  }

  pauseAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  resumeAutoplay() {
    if (this.isAutoplayEnabled && !this.autoplayTimer) {
      this.startAutoplay();
    }
  }

  resetAutoplay() {
    this.pauseAutoplay();
    this.startAutoplay();
  }

  destroy() {
    this.pauseAutoplay();
    // Remove event listeners would go here if needed for cleanup
  }
}

// Initialize all sliders on page
function initEcoFSliders() {
  const galleries = document.querySelectorAll('.ecof-slider-gallery');
  galleries.forEach(gallery => {
    if (!gallery.dataset.initialized) {
      new EcoFImageSlider(gallery);
      gallery.dataset.initialized = 'true';
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initEcoFSliders);
} else {
  initEcoFSliders();
}

// Shopify Theme Editor support
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    const gallery = event.target.querySelector('.ecof-slider-gallery');
    if (gallery) {
      gallery.dataset.initialized = 'false';
      initEcoFSliders();
    }
  });

  document.addEventListener('shopify:section:unload', function(event) {
    const gallery = event.target.querySelector('.ecof-slider-gallery');
    if (gallery && gallery.sliderInstance) {
      gallery.sliderInstance.destroy();
    }
  });
}
